import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  FormHelperText
} from '@mui/material';
import { Edit, Save } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import { Category, Customer, Exporter } from '../../types/exporter';

// ============================================================================
// Types
// ============================================================================

export interface EditExporterFormData {
  displayName: string;
  description?: string;
  network?: string;
  customerId: number | null;
  categoryId: number | null;
  enabled: boolean;
}

interface EditExporterDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (id: number, data: EditExporterFormData) => Promise<boolean>;
  exporter: Exporter | null;
  categories?: Category[];
  customers?: Customer[];
}

// ============================================================================
// Component
// ============================================================================

export const EditExporterDialog: React.FC<EditExporterDialogProps> = ({
  open,
  onClose,
  onSave,
  exporter,
  categories = [],
  customers = []
}) => {
  const theme = useTheme();

  // Form state
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [network, setNetwork] = useState('');
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(true);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Populate form when exporter changes
  useEffect(() => {
    if (exporter) {
      setDisplayName(exporter.display_name || exporter.name || '');
      setDescription(exporter.description || '');
      setNetwork(exporter.network || '');
      setCustomerId(exporter.customer_id);
      setCategoryId(exporter.category_id);
      setEnabled(exporter.enabled);
      setHasChanges(false);
    }
  }, [exporter]);

  // Track changes
  useEffect(() => {
    if (exporter) {
      const changed =
        displayName !== (exporter.display_name || exporter.name || '') ||
        description !== (exporter.description || '') ||
        network !== (exporter.network || '') ||
        customerId !== exporter.customer_id ||
        categoryId !== exporter.category_id ||
        enabled !== exporter.enabled;
      setHasChanges(changed);
    }
  }, [displayName, description, network, customerId, categoryId, enabled, exporter]);

  // Reset form when dialog closes
  const handleClose = () => {
    setDisplayName('');
    setDescription('');
    setNetwork('');
    setCustomerId(null);
    setCategoryId(null);
    setEnabled(true);
    setError('');
    setHasChanges(false);
    onClose();
  };

  // Handle form submission
  const handleSave = async () => {
    if (!exporter) return;

    setSubmitting(true);
    setError('');

    try {
      const formData: EditExporterFormData = {
        displayName: displayName || exporter.name,
        description: description || undefined,
        network: network || undefined,
        customerId,
        categoryId,
        enabled
      };

      const success = await onSave(exporter.id, formData);
      if (success) {
        handleClose();
      } else {
        setError('Failed to save changes');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save changes';
      setError(message);
      console.error('Save exporter failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'up':
        return 'success';
      case 'down':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!exporter) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '450px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Edit sx={{ mr: 1 }} />
            Edit Exporter
          </Box>
          <Chip
            label={exporter.status.toUpperCase()}
            color={getStatusColor(exporter.status)}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Read-only Connection Info */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Connection Details (Read Only)
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: theme.palette.grey[50],
              borderRadius: 1,
              mb: 3
            }}
          >
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Host
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {exporter.target_host?.split(':')[0] || exporter.name}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Port
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {exporter.target_host?.split(':')[1] || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Prometheus Job
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {exporter.prometheus_job}
                </Typography>
              </Box>
              {exporter.last_scrape && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {exporter.collection_method === 'push' ? 'Last Data Received' : 'Last Scrape'}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(exporter.last_scrape).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                Host and port cannot be changed after creation as this would break the Prometheus configuration.
                To monitor a different endpoint, create a new exporter and delete this one.
              </Typography>
            </Alert>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Editable Details */}
          <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
            Exporter Details
          </Typography>

          <TextField
            fullWidth
            label="Display Name"
            placeholder="e.g., Production Web Server"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText="Friendly name shown in the dashboard"
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Description"
            placeholder="e.g., Main web server hosting customer portal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            helperText="Optional notes about this exporter"
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Network"
            placeholder="e.g., DMZ, Internal, Customer-VPN"
            value={network}
            onChange={(e) => setNetwork(e.target.value)}
            helperText="Optional network zone identifier"
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                value={customerId ?? ''}
                onChange={(e) => setCustomerId(e.target.value === '' ? null : Number(e.target.value))}
                label="Customer"
              >
                <MenuItem value="">
                  <em>Internal / No Customer</em>
                </MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.cust_id} value={customer.cust_id}>
                    {customer.cust_name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Assign to a customer for billing/organization</FormHelperText>
            </FormControl>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryId ?? ''}
                onChange={(e) => setCategoryId(e.target.value === '' ? null : Number(e.target.value))}
                label="Category"
              >
                <MenuItem value="">
                  <em>No Category</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Group by category for filtering</FormHelperText>
            </FormControl>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Enable/Disable Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: enabled ? theme.palette.success.light + '20' : theme.palette.grey[100],
              borderRadius: 1,
              border: `1px solid ${enabled ? theme.palette.success.light : theme.palette.grey[300]}`
            }}
          >
            <Box>
              <Typography variant="subtitle2">
                Monitoring Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {enabled
                  ? 'This exporter is actively being monitored'
                  : 'Monitoring is disabled for this exporter'}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  color="success"
                />
              }
              label={enabled ? 'Enabled' : 'Disabled'}
            />
          </Box>

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges}
          loading={submitting}
          startIcon={<Save />}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditExporterDialog;
